import { SimpleGrid } from '@chakra-ui/react';
import {
  IRefundSchema,
  RefundReasonEnum,
  REFUND_STATUS_DISPLAY,
} from '@monetize/types/app';
import { formatCurrency, toDateShort } from '@monetize/utils/core';
import { ROUTES } from '../../../constants';
import { MFormFieldReadOnly } from '../MFormFieldReadOnly';
import { LookupTriggerContent } from './LookupTriggerContent';
import { RecordLookupPopover } from './RecordLookupPopover';
import { RecordLookupPopoverHeader } from './RecordLookupPopoverHeader';

const refundReadableErrorMessage: Record<string, string> = {
  lost_or_stolen_card: 'The card was reported lost or stolen.',
  expired_or_canceled_card: 'The card has expired or was canceled.',
  charge_for_pending_refund_disputed:
    'The charge for the pending refund was disputed.',
  insufficient_funds: 'There are insufficient funds available.',
  declined: 'The transaction was declined.',
  merchant_request: 'Refund requested by the merchant.',
  unknown: 'The reason for the refund failure is unknown.',
};

export interface RefundLookupPopoverProps {
  value: string;
  displayLabel?: string;
}

export function RefundLookupPopover({
  value,
  displayLabel,
}: RefundLookupPopoverProps) {
  if (!value) {
    return null;
  }
  return (
    <RecordLookupPopover
      recordId={value}
      endpoint={`/api/refunds/${value}`}
      buttonProps={{ lineClamp: 3 }}
      renderRecord={(record) => {
        const refund = record as IRefundSchema;
        const isFailed = refund.status === RefundReasonEnum.FAILED;
        return (
          <>
            <RecordLookupPopoverHeader
              recordId={refund.id}
              href={ROUTES.getRefundView({
                accountId: refund.accountId,
                refundId: refund.id,
              })}
              recordName={refund.id}
              displayStatus={REFUND_STATUS_DISPLAY[refund.status]}
            />

            <SimpleGrid columns={2} gap={2} mt={2}>
              <MFormFieldReadOnly label="Amount">
                {formatCurrency(refund.amount, { currency: refund.currency })}
              </MFormFieldReadOnly>
              <MFormFieldReadOnly label="Settled Date">
                {toDateShort(refund.settledDate)}
              </MFormFieldReadOnly>
              <MFormFieldReadOnly label="Gateway Reference">
                {refund.gatewayReference || '-'}
              </MFormFieldReadOnly>
              <MFormFieldReadOnly label="Description">
                {refund.description || '-'}
              </MFormFieldReadOnly>
              {isFailed && refund.errorCode && (
                <MFormFieldReadOnly label="Error Code">
                  {refund.errorCode}
                </MFormFieldReadOnly>
              )}
              {isFailed && refund.errorMessage && (
                <MFormFieldReadOnly label="Error">
                  {refundReadableErrorMessage[refund.errorMessage] ??
                    refund.errorMessage}
                </MFormFieldReadOnly>
              )}
            </SimpleGrid>
          </>
        );
      }}
    >
      <LookupTriggerContent displayLabel={displayLabel} value={value} />
    </RecordLookupPopover>
  );
}
